import * as React from "react";
import ContentCard from "../ui-library/content-card/content-card";
import CardContent from "../ui-library/content-card/card-content";
import { pushActionableClickEvent, EventCategory } from "../../utils/analytics";
import { useSubmitFeedback } from "../../hooks/mutations/feedback/use-feedback-hooks";
import { Topic } from "../../types/topics";
import Actionable from "../ui-library/actionable/actionable";
import styles from "./feedback.styles";

interface FeedbackProps {
  intentName: string;
  topic: Topic;
}

const Feedback: React.FC<FeedbackProps> = ({ intentName, topic }) => {
  const [feedback, setFeedback] = React.useState<
    "POSITIVE" | "NEGATIVE" | "NEUTRAL" | undefined
  >();

  const [message, setMessage] = React.useState<string>("");
  const [isSubmitted, setSubmitted] = React.useState<boolean>(false);

  const { submitFeedback } = useSubmitFeedback(topic);

  React.useEffect(() => {
    if (window) {
      const historyActionJson = localStorage.getItem(`${intentName}_feedback`);
      if (historyActionJson) {
        const historyAction = JSON.parse(historyActionJson);
        if (historyAction?.feedback) {
          setFeedback(historyAction.feedback);
        }
        if (historyAction?.isSubmitted !== undefined) {
          setSubmitted(historyAction.isSubmitted);
        }
      }
    }
  }, []);

  const storeActions = () => {
    if (window) {
      localStorage.setItem(
        `${intentName}_feedback`,
        JSON.stringify({
          feedback,
          isSubmitted,
        })
      );
    }
  };

  const handleUpVote = () => {
    setFeedback("POSITIVE");
    setSubmitted(true);
    submitFeedback({ intentName, feedback: "POSITIVE" });
    pushActionableClickEvent({
      category: EventCategory.Section_Feedback,
      action: "Click - Yes",
      label: intentName,
    });
    storeActions();
  };

  const handleDownVote = () => {
    setFeedback("NEGATIVE");
    submitFeedback({ intentName, feedback: "NEGATIVE" });
    pushActionableClickEvent({
      category: EventCategory.Section_Feedback,
      action: "Click - No",
      label: intentName,
    });
    storeActions();
  };

  const handleGiveFeedback = () => {
    setFeedback("NEUTRAL");
    pushActionableClickEvent({
      category: EventCategory.Section_Feedback,
      action: "Click - Give feedback",
      label: intentName,
    });
    storeActions();
  };

  const handleSubmitMessage = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);
    if (message.trim() !== "") {
      submitFeedback({ intentName, message });
    }
    pushActionableClickEvent({
      category: EventCategory.Section_Feedback,
      action: "Click - Submit message",
      label: message,
    });
    storeActions();
  };

  const handleCancel = () => {
    if (feedback === "NEGATIVE") {
      setSubmitted(true);
    }
    if (feedback === "NEUTRAL") {
      setFeedback(undefined);
    }
  };

  return (
    <ContentCard>
      <CardContent isNarrow={true}>
        {!feedback && (
          <div>
            <h2 css={styles.heading}>Did you find this helpful?</h2>
            <div css={styles.ctas}>
              <div css={styles.buttonContainer}>
                <button onClick={handleUpVote} css={styles.feedbackButton}>
                  <span css={[styles.buttonIcon, styles.iconLike]} />
                  Yes
                </button>
                <button onClick={handleDownVote} css={styles.feedbackButton}>
                  <span css={[styles.buttonIcon, styles.iconDislike]} />
                  No
                </button>
              </div>
              <div css={styles.buttonSeparator}>or</div>
              <button
                onClick={handleGiveFeedback}
                css={[styles.feedbackButton, styles.messageButton]}
              >
                <span css={[styles.buttonIcon, styles.iconMessage]} />
                Give feedback
              </button>
            </div>
          </div>
        )}

        {(feedback === "NEGATIVE" || feedback === "NEUTRAL") && !isSubmitted && (
          <form onSubmit={handleSubmitMessage}>
            <h2 css={styles.heading}>Let us know how we can improve</h2>
            <textarea
              css={styles.textArea}
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <div css={{ display: "flex" }}>
              <button
                type="submit"
                aria-label="Submit"
                css={styles.submitButton}
              >
                Submit
              </button>
              <Actionable
                role="button"
                ariaLabel="Cancel"
                onClick={handleCancel}
                css={styles.cancelButton}
                analytics={{
                  category: EventCategory.Section_Feedback,
                  action: "Click - Cancel send message",
                  label: "Cancel",
                }}
              >
                Cancel
              </Actionable>
            </div>
          </form>
        )}

        {isSubmitted && (
          <h2 css={styles.standAloneHeading}>Thanks for your feedback.</h2>
        )}
      </CardContent>
    </ContentCard>
  );
};

export default Feedback;
